import { Await, Link } from '@remix-run/react'
import Button from '../buttons/button'
import { Counter, Tag } from '../badgesAndTags'
import {
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import { CopyIcon, XIcon, CaretLeft, ArrowRightIcon } from '../icons'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { Suspense, useRef } from 'react'
import RollupTemplate from '../common/metadataTemplates/rollupTemplate.tsx'
import CIDsTable from '../common/cidsTable.tsx'
import SignersTable from '../common/signersTable.tsx'
import { AttestationType, eventData, nft } from '#types/loreTypes.tsx'
import {
	formatMetadataType,
	getEventURL,
	getShareAttestationToXURL,
	mapIcon,
	mapSigners,
	mapStatus,
} from '#app/utils/misc.tsx'
import InternalLink from '../common/internalLink.tsx'
import learnmorerollups from '#images/learnmorerollups.webp'
import ContentTile from '../content/ContentTile.tsx'

const formatDate = (date: Date) => {
	try {
		const options = {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		}
		//@ts-ignore
		return date.toLocaleDateString('en-US', options)
	} catch (e) {
		return 'Unknown'
	}
}

const getNFTForMetadataEntry = (
	nftData: nft[],
	nftAttestation: AttestationType,
) => {
	if (!nftData) return undefined
	return nftData.find(
		nft =>
			nftAttestation.references &&
			nftAttestation.references.entry &&
			(nftAttestation.references.entry.nft.contractAddress != undefined
				? nft.contract.address &&
					nftAttestation.references.entry.nft.contractAddress &&
					nft.contract.address.toLowerCase() ==
						nftAttestation.references.entry.nft.contractAddress.toLowerCase()
				: true) &&
			nft.tokenId == nftAttestation.references.entry.nft.tokenId,
	)
}

const createCIDTableData = (
	metadataEntry: AttestationType,
	nftAttestations: AttestationType[],
	nftData: nft[],
) => {
	const tableData = []
	if (metadataEntry) {
		if (nftAttestations && nftAttestations.length > 0) {
			nftAttestations
				.filter(nftAttestation => nftAttestation.references.type != 'Rollup')
				.forEach(nftAttestation => {
					const nft = getNFTForMetadataEntry(nftData, nftAttestation)
					tableData.push({
						cid: nftAttestation.references.attestationIPFSCID || 'Pending',
						referenceCid:
							nftAttestation.references.attestationIPFSCID || 'Pending',
						name: nft ? nft.name : 'Unknown name',
						format: nft ? nft.contentType : 'Unknown format',
						date: nftAttestation.createdAt,
						src: nft ? nft.previewImage : '',
						isVerified: undefined,
						chain: nftAttestation.references.entry
							? nftAttestation.references.entry.chain.name
							: nftAttestation.references.chain
								? nftAttestation.references.chain.name
								: '',
						contractAddress:
							nftAttestation.references.entry &&
							nftAttestation.references.entry.nft
								? nftAttestation.references.entry.nft.contractAddress
								: nftAttestation.references.nft
									? nftAttestation.references.nft.contractAddress
									: '',
						tokenId:
							nftAttestation.references.entry &&
							nftAttestation.references.entry.nft
								? nftAttestation.references.entry.nft.tokenId
								: nftAttestation.references.nft
									? nftAttestation.references.nft.tokenId
									: '',
						status: mapStatus(nftAttestation),
						attestation: nftAttestation,
					})
				})
		}
		return tableData
	} else {
		return tableData
	}
}

export default function MetadataProfile({
	event,
	nftData,
	cid,
	metadataEntry,
	nftAttestations,
	atomicLoreUrl,
}: {
	event: eventData
	nftData: nft[]
	cid: string
	metadataEntry: AttestationType
	nftAttestations: AttestationType[]
	atomicLoreUrl: string
}) {
	const tooltipCopyIPFS = useRef<TooltipRefProps>(null)
	const tooltipCopyArweave = useRef<TooltipRefProps>(null)

	return (
		<div className="mx-auto mb-16 flex h-full w-full max-w-[1440px] flex-1 flex-col px-4 md:px-12 lg:px-28">
			<div className="hidden md:block">
				<div className="mb-8 mt-16 flex flex-col items-start justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
					<InternalLink to={'/'} prefetch="none">
						<Button variant="tertiary">
							<div className="flex items-center">
								<CaretLeft />
								Home
							</div>
						</Button>
					</InternalLink>
				</div>
				<div className="w-fit">
					{!metadataEntry.isRevoked && (
						<Tag
							icon={mapIcon(
								metadataEntry.template.slug,
								'large',
								metadataEntry.properties?.fileType || '',
							)}
							showIcon={true}
						>
							{formatMetadataType(metadataEntry.template.slug)}
						</Tag>
					)}
				</div>
				<div className="mt-4 flex flex-row items-center justify-between">
					<TypographyHeading
						size="large"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>{`${metadataEntry.isRevoked ? 'Revoked ' : ''}Rollup`}</TypographyHeading>
					<div className="flex flex-row gap-x-2">
						{!metadataEntry.isRevoked && (
							<Link
								to={getShareAttestationToXURL(metadataEntry, atomicLoreUrl)}
								target="_blank"
							>
								<Button variant="secondary" leftIcon={<XIcon />}>
									Share
								</Button>
							</Link>
						)}
						<Link to={getEventURL(metadataEntry)}>
							<Button variant="primary">View event page</Button>
						</Link>
					</div>
				</div>
			</div>
			{!metadataEntry.isRevoked && (
				<div className="md:hidden">
					<div className="mt-16 w-fit">
						<Tag icon={mapIcon(metadataEntry.template.slug)} showIcon={true}>
							{formatMetadataType(metadataEntry.template.slug)}
						</Tag>
					</div>
					<div className="mt-4 flex flex-row items-center justify-start">
						<TypographyHeading
							size="large"
							isBold={true}
						>{`${metadataEntry.isRevoked ? 'Revoked ' : ''}Rollup`}</TypographyHeading>
					</div>
				</div>
			)}
			{!metadataEntry.isRevoked && metadataEntry.createdAt && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Timestamp:{' '}
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{formatDate(new Date(metadataEntry.createdAt))}
					</TypographyLabel>
				</div>
			)}
			{!metadataEntry.isRevoked && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
					>
						IPFS CID:
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{cid}
					</TypographyLabel>
					<div id="copyIPFS" className="flex flex-row">
						<CopyIcon
							className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
							onClick={() => {
								navigator.clipboard.writeText(cid)
								tooltipCopyIPFS.current?.open({
									anchorSelect: '#copyIPFS',
									content: 'Copied!',
								})
								setTimeout(() => {
									tooltipCopyIPFS.current?.close()
								}, 2000)
							}}
						/>
						<Tooltip imperativeModeOnly ref={tooltipCopyIPFS} />
					</div>
				</div>
			)}
			{!metadataEntry.isRevoked && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Arweave Transaction:
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark truncate"
					>
						{metadataEntry.references.attestationArweaveTxId}
					</TypographyLabel>
					<div id="copyArweave" className="flex flex-row">
						<CopyIcon
							className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
							onClick={() => {
								navigator.clipboard.writeText(
									metadataEntry.references.attestationArweaveTxId
										? metadataEntry.references.attestationArweaveTxId
										: '',
								)
								tooltipCopyArweave.current?.open({
									anchorSelect: '#copyArweave',
									content: 'Copied!',
								})
								setTimeout(() => {
									tooltipCopyArweave.current?.close()
								}, 2000)
							}}
						/>
						<Tooltip imperativeModeOnly ref={tooltipCopyArweave} />
					</div>
				</div>
			)}
			{!metadataEntry.isRevoked && (
				<>
					<div className="mt-16 flex h-full w-full flex-col rounded border border-textInactiveLight bg-uiTertiaryLight dark:border-textInactiveDark dark:bg-uiTertiaryDark">
						<RollupTemplate rollup={metadataEntry} />
					</div>
					<div className="mt-16 flex flex-col">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Event
						</TypographyHeading>
						<InternalLink to={getEventURL(metadataEntry)}>
							<ContentTile
								title={event?.eventTitle}
								description={`Event • Rollup • ${
									nftAttestations
										? nftAttestations.filter(
												nftAttestation =>
													nftAttestation.references.type != 'Rollup',
											).length
										: 0
								} items`}
								image={event?.eventImageUrl}
								isVerified={true}
								showCaret={true}
								showBorder={false}
								variant="art"
							/>
						</InternalLink>
					</div>
					<div className="mt-16 flex flex-1 items-center gap-x-4">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Associated NFTs
						</TypographyHeading>
						<Counter
							size="small"
							children={
								nftAttestations
									? nftAttestations.filter(
											nftAttestation =>
												nftAttestation.references.type != 'Rollup',
										).length
									: 1
							}
						/>
					</div>
					<div className="pt-6">
						<Suspense
							fallback={
								<CIDsTable data={createCIDTableData(metadataEntry, [], [])} />
							}
						>
							<Await resolve={nftAttestations}>
								{nftAttestations => {
									return (
										<Suspense
											fallback={
												<CIDsTable
													data={createCIDTableData(metadataEntry, [], [])}
												/>
											}
										>
											<Await resolve={nftData}>
												{nftData => {
													return (
														<CIDsTable
															data={createCIDTableData(
																metadataEntry,
																nftAttestations,
																nftData,
															)}
														></CIDsTable>
													)
												}}
											</Await>
										</Suspense>
									)
								}}
							</Await>
						</Suspense>
					</div>
					<div className="mt-16 flex flex-1 items-center gap-x-4">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Signers
						</TypographyHeading>
						<Counter size="small" children={1} />
					</div>
					<div className="pt-6">
						<SignersTable data={mapSigners(metadataEntry)}></SignersTable>
					</div>
				</>
			)}
			<div className="bg-white dark:bg-black">
				<div className="mx-auto mb-24 mt-32 flex max-w-[1440px] flex-col items-center justify-center">
					<div className="flex flex-col items-center gap-x-6 gap-y-6 rounded-md bg-uiSecondaryLight p-4 dark:bg-uiSecondaryDark sm:flex-row">
						<img
							src={learnmorerollups}
							alt="Learn More"
							className="w-full rounded-md object-cover sm:h-24 sm:w-24"
						/>
						<div className="flex flex-col">
							<TypographyParagraph
								size="large"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Discover the Power of Roll-up Attestations
							</TypographyParagraph>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Are you an artist or curator with thousands of exhibition
								records that need to be added onchain? Our new Roll-Up Feature
								allows you to securely sign bulk imports from spreadsheets or
								databases.
							</TypographyParagraph>
						</div>
						<Link
							to="https://www.atomicform.com/rollup"
							target="_blank"
							className="w-full sm:w-fit"
						>
							<Button
								variant="secondary"
								size="medium"
								rightIcon={<ArrowRightIcon />}
								additionalClasses="whitespace-nowrap w-full"
							>
								Learn More
							</Button>
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}
